import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, ChevronDown, ChevronRight, HelpCircle, BookOpen, CreditCard, Users, Zap, Shield, Clock, Menu, X } from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'About Us', path: '/about' },
    { label: 'Resources', path: '/resources' },
    { label: 'Contact', path: '/contact' },
    { label: 'FAQ', path: '/faq' }
  ];

  const faqCategories = [
    {
      category: 'Getting Started',
      icon: BookOpen,
      color: 'purple',
      faqs: [
        {
          question: 'What is Edpies?',
          answer: 'Edpies is the UK\'s growing AI-powered interactive learning platform designed for students from 7+ entry exams through to A-Levels. Our adaptive technology personalises learning paths based on each student\'s unique strengths, weaknesses, and learning style.'
        },
        {
          question: 'How do I create an account?',
          answer: 'Simply click "Get Started" on the homepage, fill in your details (name, email, password), and select whether you\'re a Student, Parent, or Tutor. You\'ll have immediate access to our learning platform.'
        },
        {
          question: 'Is Edpies suitable for my child\'s age group?',
          answer: 'Yes! Edpies covers all major UK educational stages: 7+ Entry, 11+ Entry (including Sutton SET, CEM, GL, and ISEB patterns), Year 9, Year 10, GCSE, and A-Levels. Each stage has age-appropriate content and difficulty levels.'
        },
        {
          question: 'Can I try Edpies before committing?',
          answer: 'Absolutely! We offer free access to explore our platform. You can browse subjects, try sample questions, and experience our AI-powered features before deciding on a subscription plan.'
        }
      ]
    },
    {
      category: 'Features & Learning',
      icon: Zap,
      color: 'cyan',
      faqs: [
        {
          question: 'What is Challenger Mode?',
          answer: 'Challenger Mode is our AI-powered adaptive difficulty feature. It starts with medium-difficulty questions and automatically adjusts based on your performance. Get answers right to increase the difficulty level (up to Level 10 - olympiad level). Build streaks to boost difficulty faster!'
        },
        {
          question: 'How does the AI personalise my learning?',
          answer: 'Our AI tracks your performance across topics, identifies patterns in your mistakes, and adjusts the difficulty and type of questions you receive. It focuses more on areas where you need improvement while maintaining confidence in your strengths.'
        },
        {
          question: 'What\'s the difference between Mock and Practice papers?',
          answer: 'Mock papers (called "Revision" papers for GCSE/A-Levels) simulate real exam conditions with timed assessments and no solutions shown during the test. Practice papers provide step-by-step solutions and explanations, perfect for learning and understanding concepts.'
        },
        {
          question: 'How many questions are in each paper?',
          answer: 'Each paper contains 25 carefully crafted questions covering the key topics for that subject and level. We have 50 Mock/Revision papers and 50 Practice papers for each subject, giving you extensive practice material.'
        },
        {
          question: 'Can I track my progress?',
          answer: 'Yes! The Student Dashboard shows your study streaks, badges earned, subjects completed, and performance analytics. Parents can also view their child\'s progress to stay informed about their learning journey.'
        }
      ]
    },
    {
      category: 'Subscription & Pricing',
      icon: CreditCard,
      color: 'green',
      faqs: [
        {
          question: 'What subscription plans are available?',
          answer: 'We offer flexible subscription plans to suit different needs: Monthly, Termly, and Annual options. Each plan provides full access to all subjects, papers, Challenger Mode, and AI features. Annual plans offer the best value.'
        },
        {
          question: 'Can I cancel my subscription anytime?',
          answer: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period. We don\'t believe in locking students into long-term commitments.'
        },
        {
          question: 'Do you offer family or group discounts?',
          answer: 'Yes! We offer special pricing for families with multiple children and for schools or tutoring centres. Contact us for more information about group discounts.'
        },
        {
          question: 'Is there a refund policy?',
          answer: 'We offer a 7-day money-back guarantee. If you\'re not satisfied with Edpies within the first 7 days of your subscription, contact us for a full refund.'
        }
      ]
    },
    {
      category: 'For Parents & Tutors',
      icon: Users,
      color: 'indigo',
      faqs: [
        {
          question: 'How can parents monitor their child\'s progress?',
          answer: 'Parents can create a Parent account linked to their child\'s profile. This provides access to progress reports, study streaks, areas of strength and improvement, and time spent learning. You\'ll have full visibility into your child\'s educational journey.'
        },
        {
          question: 'What features are available for tutors?',
          answer: 'Tutors have access to an Admin Panel where they can upload custom questions, view student statistics, and manage their tutoring content. They can also access the 1:1 Tutoring collaboration feature to connect with students.'
        },
        {
          question: 'Can tutors create their own question banks?',
          answer: 'Yes! The Admin Question Bank feature allows tutors to add custom questions with explanations, set difficulty levels, and organise content by subject and topic. This helps tutors provide personalised practice material for their students.'
        }
      ]
    },
    {
      category: 'Technical & Support',
      icon: Shield,
      color: 'rose',
      faqs: [
        {
          question: 'What devices can I use Edpies on?',
          answer: 'Edpies works on any device with a modern web browser - desktop computers, laptops, tablets, and smartphones. Our responsive design ensures a great experience on any screen size.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Absolutely. We take data security seriously. All data is encrypted, we follow GDPR guidelines, and we never share personal information with third parties. Your child\'s learning data is protected and private.'
        },
        {
          question: 'What if I need help or have a question?',
          answer: 'Our support team is available Monday-Friday 9am-6pm GMT and Saturdays 10am-2pm GMT. You can reach us via the Contact page, email at info@edpies.com, or phone. We typically respond within 24 hours.'
        },
        {
          question: 'Do I need to download anything?',
          answer: 'No downloads required! Edpies is a web-based platform that runs entirely in your browser. Just visit our website, log in, and start learning immediately.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIdx, faqIdx) => {
    const key = `${categoryIdx}-${faqIdx}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <EdpiesLogo size="sm" />
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  variant="ghost" 
                  onClick={() => navigate(item.path)}
                  className={`text-slate-300 hover:text-white hover:bg-slate-800 ${item.path === '/faq' ? 'text-purple-400' : ''}`}
                >
                  {item.label}
                </Button>
              ))}
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
              >
                Sign In
              </Button>
              <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <Button 
                size="sm"
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-xs px-3"
              >
                Sign In
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white p-2"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden border-t border-slate-800/50 py-3"
            >
              <div className="flex flex-wrap gap-2">
                {navItems.map((item) => (
                  <Button 
                    key={item.path}
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`text-slate-300 hover:text-white ${item.path === '/faq' ? 'text-purple-400' : ''}`}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/30 mb-6"
          >
            <HelpCircle className="h-10 w-10 text-white" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6" 
            style={{ fontFamily: 'Space Grotesk' }}
          >
            Frequently Asked <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Questions</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.4 }} 
            className="text-xl text-slate-400 max-w-2xl mx-auto"
          >
            Find answers to common questions about Edpies. Can't find what you're looking for? <span className="text-purple-400 cursor-pointer hover:underline" onClick={() => navigate('/contact')}>Contact us</span>.
          </motion.p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {faqCategories.map((category, categoryIdx) => (
              <motion.div
                key={categoryIdx}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: categoryIdx * 0.1 }}
              >
                <Card className="bg-slate-800/50 border-slate-700/50 overflow-hidden">
                  <CardContent className="p-0">
                    {/* Category Header */}
                    <div className={`p-6 bg-gradient-to-r from-${category.color}-900/30 to-${category.color}-800/20 border-b border-slate-700/50`}>
                      <div className="flex items-center gap-3">
                        <div className={`bg-${category.color}-500/20 p-3 rounded-xl`}>
                          <category.icon className={`h-6 w-6 text-${category.color}-400`} />
                        </div>
                        <h2 className="text-xl font-bold text-white">{category.category}</h2>
                      </div>
                    </div>

                    {/* FAQ Items */}
                    <div className="divide-y divide-slate-700/50">
                      {category.faqs.map((faq, faqIdx) => {
                        const isOpen = openIndex === `${categoryIdx}-${faqIdx}`;
                        return (
                          <motion.div
                            key={faqIdx}
                            initial={false}
                            className="cursor-pointer"
                            onClick={() => toggleFAQ(categoryIdx, faqIdx)}
                          >
                            <div className={`p-5 flex items-center justify-between hover:bg-slate-700/20 transition-colors ${isOpen ? 'bg-slate-700/30' : ''}`}>
                              <h3 className="text-white font-medium pr-4">{faq.question}</h3>
                              <motion.div
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronDown className={`h-5 w-5 ${isOpen ? 'text-purple-400' : 'text-slate-400'}`} />
                              </motion.div>
                            </div>
                            <motion.div
                              initial={false}
                              animate={{ 
                                height: isOpen ? 'auto' : 0,
                                opacity: isOpen ? 1 : 0
                              }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <p className="px-5 pb-5 text-slate-400 leading-relaxed">
                                {faq.answer}
                              </p>
                            </motion.div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 px-4">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="max-w-3xl mx-auto text-center"
        >
          <Card className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-purple-500/20">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Still Have Questions?</h2>
              <p className="text-slate-400 mb-6">Our team is here to help. Reach out and we'll get back to you within 24 hours.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/contact')} 
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  Contact Us <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/auth')} 
                  variant="outline" 
                  size="lg"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Get Started Free
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 Edpies</p>
          <div className="flex gap-4">
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/')}>Home</Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/about')}>About</Button>
            <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300" onClick={() => navigate('/brand')}>Brand Guidelines</Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => navigate('/contact')}>Contact</Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
